import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaBullhorn,
  FaEdit,
  FaTrash,
  FaPlus,
  FaUsers,
  FaPaperclip,
  FaSearch,
  FaExclamationCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import NoticeForm from "../../Components/forms/NoticeForm";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function NoticeList() {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { token } = useAuth();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/notices`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItems(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchItems();
  }, [token]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this notice?")) {
      try {
        await axios.delete(`${server}/notices/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Notice deleted successfully");
        fetchItems();
      } catch (error) {
        toast.error("Error deleting notice");
      }
    }
  };

  const filteredNotices = items.filter(
    (item) =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.targetAudience?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400 rounded-xl shadow-xs">
            <FaBullhorn size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">School Notice Board</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Broadcast announcements to students, parents, teachers, and staff</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedItem(null);
              setIsModalOpen(true);
            }}
            className="bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Post New Notice
          </button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Active Notices</span>
          <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">{items.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">High Priority Notices</span>
          <div className="text-xl font-black text-amber-600 dark:text-amber-400 mt-0.5">
            {items.filter((i) => i.isImportant).length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs col-span-2 sm:col-span-1">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Search Filter</span>
          <div className="text-xs font-bold text-gray-700 dark:text-slate-300 mt-1 truncate">
            {searchTerm || "Showing All"}
          </div>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="text-xs font-black text-gray-700 dark:text-slate-300">
          Notices Register ({filteredNotices.length})
        </div>

        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search Notice Title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
          />
        </div>
      </div>

      {/* High-Density Table Display */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Title</th>
                <th className="p-2.5">Content Snippet</th>
                <th className="p-2.5">Target Audience</th>
                <th className="p-2.5">Posted Date</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-rose-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-600 mx-auto mb-2"></div>
                    Loading notice board...
                  </td>
                </tr>
              ) : filteredNotices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-400 italic">
                    No matching notices found.
                  </td>
                </tr>
              ) : (
                filteredNotices.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 dark:hover:bg-slate-800/40 transition ${
                      item.isImportant ? "bg-rose-50/40 dark:bg-rose-950/20" : ""
                    }`}
                  >
                    <td className="p-2.5 font-black text-gray-900 dark:text-white">
                      <div className="flex items-center gap-1.5">
                        {item.isImportant && (
                          <FaExclamationCircle className="text-rose-500 shrink-0" title="High Priority Notice" />
                        )}
                        <span>{item.title}</span>
                      </div>
                    </td>
                    <td className="p-2.5 text-gray-500 dark:text-slate-400 font-medium max-w-xs truncate" title={item.content}>
                      {item.content}
                    </td>
                    <td className="p-2.5 font-bold">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-md text-[10px]">
                        {item.targetAudience || "All"}
                      </span>
                    </td>
                    <td className="p-2.5 text-gray-500 font-medium whitespace-nowrap">
                      {new Date(item.postedDate || item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        {item.attachmentUrl && (
                          <a
                            href={item.attachmentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 text-blue-600 bg-blue-50 dark:bg-blue-950 rounded-lg hover:bg-blue-100 transition"
                            title="View Attachment"
                          >
                            <FaPaperclip size={11} />
                          </a>
                        )}
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setIsModalOpen(true);
                          }}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Edit Notice"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Notice"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <NoticeForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        notice={selectedItem}
        noticeToEdit={selectedItem}
        onSuccess={fetchItems}
      />
    </div>
  );
}
